import {Injectable} from '@angular/core';
import {BehaviorSubject, forkJoin, Observable, of} from 'rxjs';
import {Favorite} from '../Models/favorite';
import {Client} from '../Models/client';
import {AccountService} from './account.service';
import {catchError, distinctUntilChanged, finalize, map, skip, switchMap, tap} from 'rxjs/operators';
import {LanguageService} from './language.service';
import {ProductConfiguration} from '../Models/product-configuration';
import { FavoriteService as _FavoriteService } from './favorite_.service';
import { ProductConfigurationService } from './product-configuration.service';
import { StorageService } from './storage.service';

/** localStorage key of the favorites added while logged out */
const OFFLINE_FAVORITES_KEY = 'favorites';

/**
 * Favorites of the visitor, keyed by product configuration id (the `productId` of the product cards).
 * Logged out: kept in localStorage. Logged in: stored through the API; the favorites added while
 * logged out are sent to the API at login, then cleared from localStorage.
 */
@Injectable({
  providedIn: 'root',
})
export class FavoriteService {

  private client: Client | null = null;
  public readonly favoritesSubject = new BehaviorSubject<Favorite[]>([]);

  public readonly favorites = this.favoritesSubject.asObservable();

  /** Number of favorites, logged in or not */
  public readonly favoritesCount$ = this.favorites.pipe(map((favorites: Favorite[]) => favorites.length));

  /** Configuration ids of the favorites, to fill the hearts of the product cards */
  public readonly favoriteConfigurationIds$ = this.favorites.pipe(
    map((favorites: Favorite[]) => favorites.map((favorite: Favorite) => this.configurationIdOf(favorite)!).filter((id) => id != null)),
  );

  /** true while the logged-in client's favorites are being fetched */
  public readonly loading$ = new BehaviorSubject<boolean>(false);

  constructor(
    private storage: StorageService,
    private favoriteService: _FavoriteService,
    private accountService: AccountService,
    private productConfigurationService: ProductConfigurationService,
    private languageService: LanguageService,
  ) {
    this.accountService.accountSubject.pipe(
      // refreshAccount() re-emits the same client (e.g. after a token refresh): only reload when it changes
      distinctUntilChanged((previous: Client | null, current: Client | null) => previous?.id === current?.id),
      switchMap((client: Client | null): Observable<Favorite[]> => {
        const wasLoggedIn = !!this.client;
        this.client = client;
        if (!client) {
          if (wasLoggedIn) {
            // logout: keep the account's favorites in this browser (sent back, deduplicated, at the next login)
            this.writeOfflineFavorites(
              this.favoritesSubject.value.map(({ productConfigurationId, productConfiguration }: Favorite) => ({
                clientId: null as any,
                productConfigurationId: productConfigurationId ?? productConfiguration?.id,
                productConfiguration,
              })),
            );
          }
          return of(this.readOfflineFavorites());
        }
        this.loading$.next(true);
        return this.syncOfflineFavorites().pipe(
          finalize(() => this.loading$.next(false)),
        );
      })
    ).subscribe((favorites: Favorite[]) => {
      this.loading$.next(false);
      this.favoritesSubject.next(favorites);
    });

    // the product configurations of the favorites come in the current language: reload them when it changes
    this.languageService.language$.pipe(
      skip(1),
      switchMap(() => (this.client ? this.refresh() : this.reloadOfflineConfigurations())),
    ).subscribe();
  }

  public isFavorite(configurationId: number): boolean {
    return !!this.findByConfigurationId(configurationId);
  }

  /** Adds the product configuration to the favorites, or removes it if it is already there */
  public toggle(configurationId: number): Observable<void> {
    return this.isFavorite(configurationId) ? this.remove(configurationId) : this.add(configurationId);
  }

  /** Re-fetches the logged-in client's favorites (no-op when logged out) */
  public refresh(): Observable<Favorite[]> {
    if (!this.client) {
      return of(this.favoritesSubject.value);
    }
    return this.loadCurrentClientFavorites().pipe(
      tap((favorites: Favorite[]) => this.favoritesSubject.next(favorites)),
    );
  }

  public add(configurationId: number): Observable<void> {
    if (this.isFavorite(configurationId)) {
      return of(undefined);
    }
    // optimistic: the heart and the counter react immediately
    this.favoritesSubject.next([
      ...this.favoritesSubject.value,
      { clientId: this.client?.id as number, productConfigurationId: configurationId },
    ]);

    if (this.client) {
      return this.favoriteService.add({ clientId: this.client.id as number, productConfigurationId: configurationId }).pipe(
        // reload to get the favorite id and its product configuration
        switchMap(() => this.refresh()),
        catchError(() => this.refresh()),
        map(() => undefined),
      );
    }

    // logged out: keep the configuration so the favorites page can display it
    return this.productConfigurationService.getByConfigurationId(configurationId).pipe(
      catchError(() => of(undefined)),
      map((productConfiguration?: ProductConfiguration) => {
        const favorites = this.favoritesSubject.value.map((favorite: Favorite) =>
          this.configurationIdOf(favorite) === configurationId ? { ...favorite, productConfiguration } : favorite,
        );
        this.favoritesSubject.next(favorites);
        this.writeOfflineFavorites(favorites);
      }),
    );
  }

  public remove(configurationId: number): Observable<void> {
    const favorite = this.findByConfigurationId(configurationId);
    if (!favorite) {
      return of(undefined);
    }
    const favorites = this.favoritesSubject.value.filter((item: Favorite) => item !== favorite);
    this.favoritesSubject.next(favorites);

    if (!this.client) {
      this.writeOfflineFavorites(favorites);
      return of(undefined);
    }
    if (favorite.id == null) {
      // added a moment ago, its id is not known yet
      return this.refresh().pipe(
        switchMap(() => this.remove(configurationId)),
      );
    }
    return this.favoriteService.delete(favorite.id).pipe(
      catchError(() => this.refresh().pipe(map(() => undefined))),
    );
  }

  /**
   * Loads the client's favorites and sends the ones added while logged out, skipping those the client already has.
   * One GET when there is nothing new to save; reloaded after saving to get the new ids and configurations.
   * They stay in localStorage if saving fails, to be retried at the next login.
   */
  private syncOfflineFavorites(): Observable<Favorite[]> {
    const offlineIds = this.readOfflineFavorites()
      .map((favorite: Favorite) => this.configurationIdOf(favorite))
      .filter((id): id is number => id != null);

    return this.loadCurrentClientFavorites().pipe(
      switchMap((existing: Favorite[]) => {
        if (!offlineIds.length || !this.client) {
          return of(existing);
        }
        const clientId = this.client.id as number;
        const existingIds = new Set(existing.map((favorite: Favorite) => this.configurationIdOf(favorite)));
        const toSave = [...new Set(offlineIds)]
          .filter((id: number) => !existingIds.has(id))
          .map((productConfigurationId: number) => ({ clientId, productConfigurationId }));
        if (!toSave.length) {
          this.writeOfflineFavorites([]);
          return of(existing);
        }
        return this.favoriteService.saveAll(toSave).pipe(
          tap(() => this.writeOfflineFavorites([])),
          switchMap(() => this.loadCurrentClientFavorites()),
          catchError(() => of(existing)),
        );
      }),
    );
  }

  /** Logged out: fetches again the product configurations kept in localStorage, in the current language */
  private reloadOfflineConfigurations(): Observable<Favorite[]> {
    const favorites = this.favoritesSubject.value;
    if (!favorites.length) {
      return of(favorites);
    }
    return forkJoin(
      favorites.map((favorite: Favorite) => {
        const configurationId = this.configurationIdOf(favorite);
        return configurationId == null
          ? of(favorite)
          : this.productConfigurationService.getByConfigurationId(configurationId).pipe(
            map((productConfiguration: ProductConfiguration) => ({ ...favorite, productConfiguration })),
            catchError(() => of(favorite)),
          );
      }),
    ).pipe(
      tap((reloaded: Favorite[]) => {
        this.favoritesSubject.next(reloaded);
        this.writeOfflineFavorites(reloaded);
      }),
    );
  }

  private loadCurrentClientFavorites(): Observable<Favorite[]> {
    return this.favoriteService.getCurrentClient().pipe(
      map((favorites: Favorite[]) => favorites ?? []),
      catchError(() => of([])),
    );
  }

  private findByConfigurationId(configurationId: number): Favorite | undefined {
    return this.favoritesSubject.value.find((favorite: Favorite) => this.configurationIdOf(favorite) === configurationId);
  }

  private configurationIdOf(favorite: Favorite): number | undefined {
    return favorite.productConfigurationId ?? favorite.productConfiguration?.id;
  }

  private readOfflineFavorites(): Favorite[] {
    try {
      const favorites = JSON.parse(this.storage.getItem(OFFLINE_FAVORITES_KEY) ?? '[]');
      return Array.isArray(favorites) ? favorites : [];
    } catch {
      return [];
    }
  }

  private writeOfflineFavorites(favorites: Favorite[]): void {
    try {
      if (favorites.length) {
        this.storage.setItem(OFFLINE_FAVORITES_KEY, JSON.stringify(favorites));
      } else {
        this.storage.removeItem(OFFLINE_FAVORITES_KEY);
      }
    } catch {
      // storage unavailable (private mode...): favorites only live for this session
    }
  }
}
